import { Injectable } from '@angular/core';
import {
  EVENT_STATUS_VALUE,
  STAND_WORKSHEET_STATUS_VALUE,
} from '@app/containers/select-event/select-event/select-event.enum';
import { lazyInject } from '@app/shared/decorators';

import { IEventStatus, ObjectId } from '@app/shared/types';
import { I18n } from '@app/i18n/strings-keys';
import { findIndex, get, head, includes, isEmpty, isEqual } from 'lodash';

import { BaseService, IServiceConfig } from '../base';
import { TokenSessionService } from '../session';
import { XprtDataService } from '../xprt-data/xprt-data.service';
import { XviDataService } from '../xvi-data/xvi-data.service';
import { ENTITY_TYPES } from '@app/shared/types/enums/entityTypes.enum';

@Injectable()
export class ItemCountService extends BaseService {
  public static config: IServiceConfig = {
    ENTITY_TYPE: ENTITY_TYPES.POS_COUNT,
    apiEndpoint: 'pos-count',
    customEntityRoute: {
      list: 'master',
    },
  };

  @lazyInject(TokenSessionService)
  public tokenSessionService: TokenSessionService;
  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;
  @lazyInject(XviDataService) public xviDataService: XviDataService;

  public async validateIsCanSubmit(eventId: ObjectId): Promise<string> {
    const siteId = this.tokenSessionService.siteId;

    const validResults = await Promise.all([
      this.validateIsEventOpened(eventId),
      this.validateStandWorksheet(eventId, siteId),
    ]);

    const errorMessages = [
      this.i18nService.instant(
        I18n.ItemCountStrings.FinalizeEventNotAvailable,
      ),
      this.i18nService.instant(
        I18n.ItemCountStrings.FinalizeStandWorksheetNotAvailable,
      ),
    ];

    const invalidIndex = findIndex(validResults, (valid) => valid);

    return get(errorMessages, invalidIndex);
  }

  public async validateIsEventOpened(eventId: ObjectId): Promise<boolean> {
    const event: IEventStatus[] = await this.xprtDataService
      .getEventDetailById(eventId)
      .toPromise();

    return !isEqual(EVENT_STATUS_VALUE.Open, get(head(event), 'status'));
  }

  public async validateStandWorksheet(
    eventId: ObjectId,
    siteId: ObjectId,
  ): Promise<boolean> {
    const query = {
      select: ['site_id', 'status'],
      filter: `(event_id eq objectid(${eventId}) and (site_id eq objectid(${siteId})) and is_active eq true)`,
    };

    const standWorkSheetData = await this.xviDataService
      .getStandWorkSheet(query)
      .toPromise();

    return (
      isEmpty(standWorkSheetData) ||
      includes(
        [
          STAND_WORKSHEET_STATUS_VALUE.Canceled,
          STAND_WORKSHEET_STATUS_VALUE.Completed,
        ],
        standWorkSheetData[0].status,
      )
    );
  }
}
